import React from 'react';

import {Jed} from 'jed';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import * as moment from 'moment';
import {sprintf} from 'sprintf-js';

/*
for now we need specify here the locales which the sp app supports
so webpack adds the moment locale file to the bundle
*/
import {DEBUG_LOCALE} from './app-envs';
import {reportToSentry} from './sentry';

// exported to be monkeypatched in the tests
/* eslint-disable */
export let storedMessages = {
  en: {
    '': '1',
  },
};

let i18n = new Jed({
  domain: '_noop',
  locale_data: {
    _noop: storedMessages['en'],
  },
});

const localeFetcher = {
  'pt-br': () =>
    // @ts-ignore
    import(
      /* webpackChunkName: "locale_pt-br" */
      'app/locale/pt_BR.po'
    ),
  'en-au': () =>
    // @ts-ignore
    import(
      /* webpackChunkName: "locale_en-au" */
      'app/locale/en_AU.po'
    ),
  es: () =>
    // @ts-ignore
    import(
      /* webpackChunkName: "locale_es" */
      'app/locale/es.po'
    ),
  ur: () =>
    // @ts-ignore
    import(
      /* webpackChunkName: "locale_ur" */
      'app/locale/ur.po'
    ),
  yo: () =>
    // @ts-ignore
    import(
      /* webpackChunkName: "locale_yo" */
      'app/locale/yo.po'
    ),
  pl: () =>
    // @ts-ignore
    import(
      /* webpackChunkName: "locale_pl" */
      'app/locale/pl.po'
    ),
};
/* eslint-enable */

function format(translated, args) {
  return argsInvolveReact(args)
    ? formatForReact(translated, args)
    : sprintf(translated, ...args);
}

function setMomentLocale(locale) {
  const normalizedLocale = locale.toLowerCase().replace('_', '-');
  // eslint-disable-next-line prefer-const
  moment.locale(normalizedLocale);
}

function loadMessages(locale: string): Promise<{[key: string]: string}> {
  // if the lang is english, we don't need fetch messages
  const fetcher =
    Object.keys(localeFetcher).indexOf(locale) >= 0
      ? localeFetcher[locale]
      : () => new Promise((resolve) => resolve(storedMessages['en']));

  return fetcher().catch(() => {
    // if the request fail, lets just use english
    return storedMessages['en'];
  });
}

export async function setLocale(
  locale: string
): Promise<void | {[key: string]: string}> {
  try {
    const messages = await loadMessages(locale);
    storedMessages[locale] = messages;

    i18n = new Jed({
      domain: 'sweetprocess-front',
      locale_data: {
        'sweetprocess-front': messages,
      },
    });
    setMomentLocale(locale);
    return messages;
  } catch (error) {
    reportToSentry(
      error,
      {
        locale: locale,
      },
      true
    );
  }
}

function formatForReact(translated, args) {
  const rv: Array<string | React.ReactElement> = [];
  let cursor = 0;
  // always re-parse, do not cache, because we change the match
  sprintf.parse(translated).forEach((match, idx) => {
    if (isString(match)) {
      rv.push(match);
    } else {
      let arg = null;

      if (match[2]) {
        arg = args[0][match[2][0]];
      } else if (match[1]) {
        arg = args[parseInt(match[1], 10) - 1];
      } else {
        arg = args[cursor++];
      }

      // this points to a react element!
      if (React.isValidElement(arg)) {
        rv.push(
          React.cloneElement(arg, {
            key: idx,
          })
        ); // not a react element, fuck around with it so that sprintf.format
        // can format it for us.  We make sure match[2] is null so that we
        // do not go down the object path, and we set match[1] to the first
        // index and then pass an array with two items in.
      } else {
        match[2] = null;
        match[1] = 1;
        rv.push(
          <span key={idx++}>{sprintf.format([match], [null, arg])}</span>
        );
      }
    }
  });
  return rv;
}

function argsInvolveReact(args) {
  if (args.some(React.isValidElement)) {
    return true;
  }

  if (args.length == 1 && isObject(args[0])) {
    return Object.keys(args[0]).some((key) => {
      return React.isValidElement(args[0][key]);
    });
  }

  return false;
}

export const gettext = (msgid: string, ...args): string => {
  let translated = i18n.gettext(msgid);

  if (args.length) {
    translated = format(translated, args);
  }

  return mark(translated);
};
export const gt = gettext;
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const gettextParseString = (msgid: string, ...args: any): string => {
  /*
    same as gettext but parse the result to string
    (useful for debugging locale inside <option> tags)
     */
  return gettext(msgid, ...args) + '';
};
export const gts = gettextParseString;

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const ngettext = (
  msgid: string,
  msgid_plural: string,
  n: number,
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  ...args: any
) => {
  return mark(format(i18n.ngettext(msgid, msgid_plural, n), [n].concat(args)));
};
export const ngt = ngettext;
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const pgettext = (msgctxt: string, msgid: string, ...args: any) => {
  return mark(format(i18n.pgettext(msgctxt, msgid), args));
};
export const pgt = pgettext;
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const npgettext = (
  msgctxt: string,
  msgid: string,
  msgid_plural: string,
  n: number,
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  ...args: any
) => {
  return mark(format(i18n.npgettext(msgctxt, msgid, msgid_plural, n), args));
};
export const npgt = npgettext;

function mark(str: string): string {
  if (!DEBUG_LOCALE) {
    return str;
  }

  const proxy = {
    $$typeof: Symbol.for('react.element'),
    type: 'span',
    key: null,
    ref: null,
    props: {
      className: 'translation-wrapper',
      style: {
        backgroundColor: '#88dc8e',
        // '#ff953d',
        border: '2px solid #5c9c60',
        borderRadius: '3px',
        margin: '-2px',
      },
      children: [str], //isArray(str) ? str : [str]
    },
    _owner: null,
    _store: {},
  };

  proxy.toString = function () {
    return '\u2705' + str + '\u2705';
  };

  return proxy as unknown as string;
}
